package nl.volleyplanner.web.controller.thuisprogramma.version_selector

import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import kotlinx.html.TagConsumer
import kotlinx.html.button
import kotlinx.html.div
import kotlinx.html.id
import kotlinx.html.js.div
import kotlinx.html.js.onChangeFunction
import kotlinx.html.js.onClickFunction
import kotlinx.html.option
import kotlinx.html.p
import kotlinx.html.select
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component.CheckboxType
import nl.volleyplanner.web.base.component.OkCancelModalComponent
import nl.volleyplanner.web.base.component.checkbox
import nl.volleyplanner.web.base.routing.NavigationManager
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaDataManager
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement

class ThuisProgrammaVersionSelectorComponent(
    val problemSlug: String,
    val thuisprogrammaDataManager: ThuisprogrammaDataManager
) : HTMLComponent() {
    private val problemVersionsState
        get() = thuisprogrammaDataManager.problemData.problemVersionsDataFlow.subscribe()

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val planningProblem = thuisprogrammaDataManager.problemData.problemDataFlow.value
        val problemVersions = problemVersionsState.value
        val selectedVersionSlug = thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState.value
        val selectedVersion = problemVersionsState.value?.firstOrNull { it.slug == selectedVersionSlug }
        val canPublishVersion = thuisprogrammaDataManager.problemData.getSelectedVersionData().canPublishVersion.value

        val createNewVersionModalComponent = componentScope.async {
            createStandaloneComponent(CreateVersionModalComponent(thuisprogrammaDataManager, problemVersionsState))
        }
        val editVersionModalComponent = componentScope.async {
            createStandaloneComponent(EditVersionModalComponent(thuisprogrammaDataManager))
        }

        val publishVersionModalComponent = componentScope.async {
            createStandaloneComponent(
                OkCancelModalComponent(
                modalTitle = "Versie publiceren",
                modalBody = {
                    p {
                        +"Hiermee worden alle wijzigingen in deze versie gepubliceerd. Doorgaan?"
                    }
                },
                onOk = {
                    thuisprogrammaDataManager.problemData.getSelectedVersionData().publishVersion()
                }
            ))
        }

        if (planningProblem == null || problemVersions == null) {
            return consumer.div(classes = "row border bg-secondary-subtle rounded p-2 mb-2 d-print-none") {
                spinner()
            }
        }

        return consumer.div(classes = "row border bg-secondary-subtle rounded p-2 mb-2 d-print-none") {
            div (classes = "col-auto") {
                select(classes = "form-select w-auto me-2") {
                    id = "vp-problem-version-selector"

                    option {
                        value = "Gepubliceerd"
                        selected = selectedVersionSlug == null
                        +"Gepubliceerd"
                    }

                    problemVersions.forEach { problemVersion ->
                        option {
                            value = problemVersion.slug
                            selected = selectedVersionSlug == problemVersion.slug

                            +problemVersion.name
                        }
                    }

                    onChangeFunction = { event ->
                        event.preventDefault()
                        // Gepubliceerd is the default value. Don't set the query parameter for this slug
                        val problemVersionSlug = (event.currentTarget as HTMLSelectElement).value
                            .takeIf { it != "Gepubliceerd" }
                        NavigationManager.setQueryParameters(mapOf("versie" to problemVersionSlug))

                        // TODO: Remove - query parameters
//                        if (problemVersionSlug == "Gepubliceerd") {
//                            NavigationManager.setQueryParameters(mapOf("versie" to null))
//                            // FIXME: With new pagerouter
//                            //getPageRouteContext().queryParameters.delete("version")
//                            thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState.value = null
//                        } else {
//                            val problemVersion = problemVersions.firstOrNull { it.slug == problemVersionSlug }
//                            NavigationManager.setQueryParameters(mapOf("versie" to problemVersion!!.slug))
//                            // FIXME: With new pagerouter
//                            //getPageRouteContext().queryParameters.set("version", problemVersion!!.slug)
//                            thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState.value =
//                                problemVersion!!.slug
//                        }
//                        // TODO: Fix with new pagerouter
//                        //getPageContext().save()
                    }
                }
            }
            div (classes = "col-auto") {
                button(classes="btn btn-primary") {
                    disabled = !canPublishVersion

                    +"Versie bewerken"

                    onClickFunction = { event ->
                        event.preventDefault()
                        componentScope.launch {
                            editVersionModalComponent.await().show(
                                problem = planningProblem,
                                versionToEdit = selectedVersion
                            )
                        }
                    }
                }
            }
            div (classes = "col-auto") {
                button(classes="btn btn-primary") {
                    disabled = !canPublishVersion

                    +"Nieuwe versie"

                    onClickFunction = { event ->
                        event.preventDefault()
                        componentScope.launch {
                            createNewVersionModalComponent.await().show()
                        }
                    }
                }
            }
            div (classes = "col-auto ms-auto") {
                val controls = thuisprogrammaDataManager.thuisprogrammaControlsState.value
                checkbox(
                    CheckboxType.Switch,
                    label = "Toon wijzigingen tov gepubliceerd",
                    htmlId = "tp-showchanges-toggle",
                    inline = true,
                    isChecked = controls.showChangesWithOtherVersion,
                    isEnabled = selectedVersionSlug != null,
                    onChangeFunction = { event ->
                        thuisprogrammaDataManager.thuisprogrammaControlsState.value = thuisprogrammaDataManager.thuisprogrammaControlsState.value.copy(
                            showChangesWithOtherVersion = (event.currentTarget as HTMLInputElement).checked
                        )
                    }
                )
                button(classes = "btn btn-primary") {
                    disabled = selectedVersionSlug == null || !canPublishVersion

                    onClickFunction = { event ->
                        event.preventDefault()
                        componentScope.launch {
                            publishVersionModalComponent.await().show()
                        }
                    }

                    +"Versie publiceren"
                }
            }
        }
    }

    override suspend fun getState() = listOf(
        thuisprogrammaDataManager.problemData.problemDataFlow.subscribe(),
        problemVersionsState,
        thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState,
        thuisprogrammaDataManager.problemData.getSelectedVersionPropertyState(componentScope) { it.canPublishVersion}
    ).combineAndLaunch(componentScope)
}